import { AxiosError } from 'axios';
import { useGet } from 'hooks/use-get';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import { useAccountState } from 'contexts/account/account-context';
import { getScenarioSummary } from 'views/bluviu-nutrition/features/scenarios/api/bluviu-nutrition';
import { GraphData, ScenarioResult, Summary, SummaryType } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';

type State = {
  isLoading: boolean;
  showGraph: boolean;
  showTopPerformers: boolean;
  summaryResult: ScenarioResult | undefined;
  summaries: Summary[] | undefined;
  displayedColumns: (keyof Summary)[];
  feedPlans: any;
  week: number | any;
  weight: number | any;
  selectedSummaryType: SummaryType;
  scenarioResultQuery: UseQueryResult<ScenarioResult, AxiosError<unknown, any>>;
  selectedFeedPlans: Summary[];
  selectedGraphData: GraphData[];
  setShowTopPerformers: React.Dispatch<React.SetStateAction<boolean>>;
  setSummaries: React.Dispatch<React.SetStateAction<Summary[] | undefined>>;
  setShowGraph: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedSummaryType: React.Dispatch<React.SetStateAction<SummaryType>>;
  setSelectedFeedPlans: React.Dispatch<React.SetStateAction<Summary[]>>;
  setSelectedGraphData: React.Dispatch<React.SetStateAction<GraphData[]>>;
  setWeek: React.Dispatch<any>;
  setWeight: React.Dispatch<any>;
};

const columns: (keyof Summary)[] = [
  'feedPlan',
  'finalWeightGrams',
  'bFcr',
  'filletFatPercent',
  'hogGainGrams',
  'globalWarmingPotentialCo2KgPerKgGrowth',
  'globalWarmingPotentialCo2KgPerKgFeed',
  'averageFeedCost',
  'feedCostOfGrowth',
  'feedCostOfHogGrowth',
  'carcassYieldPercent',
  'daysToReach',
  'weightGainGrams',
  'feedIntakeGrams',
];

const ScenarioResultContext = React.createContext<State | undefined>(undefined);

type ScenarioResultProviderProps = {
  children: React.ReactNode;
};

const ScenarioResultProvider: React.FC<ScenarioResultProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [summaryResult, setSummaryResult] = useState<ScenarioResult>();
  const [selectedFeedPlans, setSelectedFeedPlans] = useState<Summary[]>([]);
  const [selectedGraphData, setSelectedGraphData] = useState<GraphData[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>();
  const [displayedColumns, setDisplayedColumns] = useState<(keyof Summary)[]>([]);
  const [selectedSummaryType, setSelectedSummaryType] = useState(SummaryType.Week);
  const [showGraph, setShowGraph] = useState<boolean>(false);
  const [showTopPerformers, setShowTopPerformers] = useState(true);
  const [week, setWeek] = useState<number | undefined>(summaryResult?.maxAllowedWeek);
  const [weight, setWeight] = useState<number | undefined>(summaryResult?.maxAllowedWeight);

  const params = useParams<ScenariosParams>();
  const { account } = useAccountState();

  const calculateDaysDifference = (
    summaries: Summary[],
    targetWeight: number
  ): { summary: Summary; daysDifference: number } | null => {
    // Group summaries by weightGainGrams using _.groupBy
    const weightGroups = _.groupBy(summaries, 'weightGainGrams');
    // Create an array of weights and their minimum daysToReach within the threshold

    const weightInfo = _.map(_.keys(weightGroups), (weight) => {
      const weightNumber = Number(weight);
      const closestSummary = _.min(weightGroups[weightNumber], 'daysToReach') as Summary;
      // Include only those weights within the threshold range
      return {
        weight: weightNumber,
        daysToReach: closestSummary.daysToReach,
        distanceFromTarget: Math.abs(weightNumber - targetWeight),
      };
    });
    // Sort weights first by daysToReach as priority and then by proximity to the targetWeight
    const sortedWeights = _.sortBy(weightInfo, ['daysToReach', 'distanceFromTarget']);

    // Ensure we have at least two weights to compare
    if (sortedWeights.length < 2) {
      return null;
    }
    // Get the closest and the next closest weights
    const closestWeight = sortedWeights[0];
    const nextClosestWeight = sortedWeights[1];

    // Find summaries for the closest and next closest weights
    const closestSummaries = weightGroups[closestWeight.weight];
    const nextClosestSummaries = weightGroups[nextClosestWeight.weight];

    // Find the summary with the minimum daysToReach for each weight
    const closestSummary = _.min(closestSummaries, 'daysToReach') as Summary;
    const nextClosestSummary = _.min(nextClosestSummaries, 'daysToReach') as Summary;

    // Calculate the difference in daysToReach
    const daysDifference = Math.round(Math.round(nextClosestSummary.daysToReach) - Math.round(closestSummary.daysToReach));

    const resultMap = new Map<Summary, number>();
    resultMap.set(closestSummary, daysDifference);

    return {
      summary: closestSummary,
      daysDifference,
    };
  };

  const getBestFeedPlans = (key: string, minOrMax: string, summary: Summary[] | undefined) => {
    const mappedAverageFeedCost = summary ? summary?.map((item) => item[key as keyof Summary]) : [];
    const sortedArray = _.sortBy(mappedAverageFeedCost);
    const groupedArray = _.groupBy(sortedArray);
    let percentage = 0;

    if (_.size(groupedArray) > 1) {
      const [bestFeedValue, secondBestValue] = Object.keys(groupedArray).slice(0, 2).map(Number);
      const diff = Number(secondBestValue) - Number(bestFeedValue);
      let result = (diff / Number(secondBestValue)) * 100;
      //Remove Negative Sign
      percentage = Number(result.toString().replace('-', ''));
    }

    if (
      summary &&
      summary.length > 0 &&
      selectedSummaryType === SummaryType.Weight &&
      _.size(groupedArray) > 1 &&
      key === 'weightGainGrams'
    ) {
      let targetWeight = weight ?? 0;
      const result = calculateDaysDifference(summary, targetWeight);

      if (result) {
        const { summary, daysDifference } = result;
        return { name: summary?.feedPlan, percentage: 0, daysToReach: daysDifference.toString().replace('-', '') };
      }
    }

    if (summary && summary.length > 0) {
      const planName =
        minOrMax === 'min'
          ? (_.min(summary, (item) => item[key as keyof Summary]) as Summary)
          : (_.max(summary, (item) => item[key as keyof Summary]) as Summary);
      return { name: planName?.feedPlan, percentage: percentage, daysToReach: planName.daysToReach };
    }
    return {};
  };

  const scenarioResultQuery = useGet(
    ['getScenarioSummary', account.company!.companyId, params.scenarioId],
    ({ queryKey }) =>
      getScenarioSummary(
        String(queryKey[1]),
        String(queryKey[2]),
        selectedSummaryType,
        selectedSummaryType === SummaryType.Week ? week : weight
      ),
    {
      enabled: Boolean(account.company?.companyId) && !!params.scenarioId,
    }
  );

  useEffect(() => {
    const data = scenarioResultQuery.data;
    if (!data) return;

    setIsLoading(false);
    setSummaryResult(data);
    setSelectedFeedPlans([]);
    setSelectedGraphData([]);

    let replaceNaNValues = data.summaries.map((obj: Summary) =>
      Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, ['feedPlan', 'feeds'].includes(key) ? value : isNaN(Number(value)) ? 0 : value])
      ) as Summary
    );
    setSummaries(_.sortBy(replaceNaNValues, (summary) => summary.feedPlan.toLowerCase()));
    setDisplayedColumns(columns);
  }, [scenarioResultQuery.data]);

  const feedPlans = useMemo(() => {
    const lowestCo2Plan = getBestFeedPlans('globalWarmingPotentialCo2KgPerKgGrowth', 'min', summaries);
    const bestBiologyPlan = getBestFeedPlans('weightGainGrams', 'max', summaries);
    const mostEconomicalPlan = getBestFeedPlans('averageFeedCost', 'min', summaries);
    return { lowestCo2Plan, bestBiologyPlan, mostEconomicalPlan };
  }, [summaries]);

  return (
    <ScenarioResultContext.Provider
      value={{
        isLoading,
        showGraph,
        showTopPerformers,
        summaryResult,
        summaries,
        displayedColumns,
        feedPlans,
        week,
        weight,
        selectedSummaryType,
        scenarioResultQuery,
        selectedFeedPlans,
        selectedGraphData,
        setSelectedSummaryType,
        setShowTopPerformers,
        setSummaries,
        setShowGraph,
        setSelectedFeedPlans,
        setSelectedGraphData,
        setWeek,
        setWeight,
      }}>
      {children}
    </ScenarioResultContext.Provider>
  );
};

function useScenarioResultState() {
  const context = useContext(ScenarioResultContext);
  if (context === undefined) {
    throw new Error('useScenarioState must be used within a ScenarioResultContext');
  }
  return context;
}

export { ScenarioResultProvider, useScenarioResultState };
